import { useEffect } from "react";
import { SystemBar } from "@/components/system/SystemBar";
import { Desktop } from "@/components/system/Desktop";
import { Dock } from "@/components/system/Dock";
import { CommandPalette } from "@/components/system/CommandPalette";
import { ActivityCenter } from "@/components/system/ActivityCenter";
import { NotificationCenter } from "@/components/system/NotificationCenter";
import { SampleDataBanner } from "@/components/system/SampleDataBanner";
import { ToastHost } from "@/components/system/ToastHost";
import { WindowManager } from "@/components/windows/WindowManager";
import { ProductPreviewModal } from "@/components/products/ProductPreview";
import { useHive } from "@/store/useHive";
import { useWorkflow } from "@/store/workflowStore";
import { useExecution } from "@/store/executionStore";
import { useAuth } from "@/auth/AuthProvider";
import { useLiveActivity } from "@/hooks/useLiveActivity";
import { useExecutionHeartbeat } from "@/hooks/useExecutionHeartbeat";
import { FullScreenLoader } from "@/components/auth/ui";

function DataError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-screen w-screen flex-col items-center justify-center gap-4 bg-[hsl(var(--bg-0))] text-app">
      <h1 className="text-lg font-semibold">Couldn't load your workspace</h1>
      <p className="max-w-sm text-center text-sm text-app-1">
        We couldn't reach the Hive API. Check your connection and try again.
      </p>
      <button
        onClick={onRetry}
        className="rounded-xl bg-[hsl(var(--accent))] px-4 py-2 text-sm font-semibold text-[hsl(var(--bg-0))]"
      >
        Retry
      </button>
    </div>
  );
}

export default function App() {
  const theme = useHive((s) => s.theme);
  const dataStatus = useHive((s) => s.dataStatus);
  const hydrate = useHive((s) => s.hydrate);
  const { me } = useAuth();
  useLiveActivity();
  useExecutionHeartbeat();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  // Mirror the session's entitlements + sample-data status into the desktop
  // store — the /me payload is the single source of truth for gating.
  useEffect(() => {
    useHive.getState().setSession(me);
    if (me) void useHive.getState().loadEarlyAccess();
  }, [me]);

  useEffect(() => {
    void hydrate();
    void useWorkflow.getState().hydrate();
    void useExecution.getState().hydrate();
  }, [hydrate]);

  if (dataStatus === "idle" || dataStatus === "loading") {
    return <FullScreenLoader label="Loading your workspace…" />;
  }
  if (dataStatus === "error") {
    return <DataError onRetry={() => void hydrate()} />;
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-[hsl(var(--bg-0))] text-app transition-colors duration-500">
      {/* base gradient wash */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[hsl(var(--bg-1))] to-[hsl(var(--bg-0))]" />

      <Desktop />
      <WindowManager />
      <SystemBar />
      <SampleDataBanner />
      <Dock />

      <CommandPalette />
      <ActivityCenter />
      <NotificationCenter />
      <ProductPreviewModal />
      <ToastHost />
    </div>
  );
}
